import React, { useEffect, useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { Form } from 'antd';
import { SERVER_IP } from 'assets/Config';
import { DATE_FORMAT } from 'constants/app-constants';
import { sendGetRequest, sendPutRequest } from 'redux/sagas/utils';
import GRNUpdatePresentational from './grn-update-presentational';
import GRNUpdateToPrint from './invoice-validation-to-print';
import { column } from '..';

const initialPageSize = 10;
const intialPageSizeOptions = [10, 15, 20];

const GRNUpdateFunctional = React.memo(() => {
	const [form] = Form.useForm();
	const [fetchingData, setFetchingData] = useState(false);
	const [updatingData, setUpdatingData] = useState(false);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [state, setState] = useState({
		visible: false,
	});
	const componentRef = React.useRef();
	const [searchKey, setSearchKey] = useState('');
	const [selectedRecordToPrint, setSelectedRecordToPrint] = useState(null);
	const [selectedRow, setSelectedRow] = useState(null);
	const [selectedViewRow, setSelectedViewRow] = useState(null);
	const [tableData, setTableData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((record) => {
			return (
				(record?.billNumber || '')?.toString()?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(moment(record?.billingDate).format(DATE_FORMAT.DD_MM_YYYY)?.toString() || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(moment(record?.deliveryDate).format(DATE_FORMAT.DD_MM_YYYY)?.toString() || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(record?.status || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(record?.customerId?.displayName || '')?.toLowerCase().includes(searchKey.toLowerCase())
			);
		});
	}, [tableData, searchKey]);

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage === 0 ? 1 : currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1) return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const onSelectChange = (selectedRowKeys) => {
		setSelectedRowKeys(selectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		columnWidth: '5%',
	};

	const handleAfterPrint = () => {
		setSelectedRecordToPrint(null);
	};

	const reactToPrintContent = React.useCallback(() => {
		return componentRef.current;
	}, []);

	const handlePrint = useReactToPrint({
		content: reactToPrintContent,
		documentTitle: 'Receipt',
		onAfterPrint: handleAfterPrint,
		removeAfterPrint: true,
	});

	useEffect(() => {
		selectedRecordToPrint && handlePrint();
	}, [selectedRecordToPrint, handlePrint]);

	useEffect(() => {
		selectedRow &&
			setState((state) => ({
				...state,
				visible: true,
			}));
	}, [selectedRow]);

	useEffect(() => {
		!state?.visible && setSelectedRow(null);
	}, [state?.visible]);

	const getBillingItems = useCallback(async () => {
		const formValues = form.getFieldsValue();
		console.log('🚀 ~ formValues:', formValues);
		await setFetchingData(true);
		const res = await sendGetRequest(null, `${SERVER_IP}stock?lrNumber=${formValues?.lrNumber}&orgId=${globalRedux.selectedOrganization._id}`);
		await setTableData(res?.data?.data || []);
		await setFetchingData(false);
	}, [setFetchingData, globalRedux?.selectedOrganization?._id]);

	const updateGRNNumber = useCallback(async () => {
		const formValues = form.getFieldsValue();
		if (!formValues?.grnNumber) {
			form.setFields([
				{
					name: 'grnNumber',
					errors: ['This field is required'],
				},
			]);
			return;
		}
		await setUpdatingData(true);
		const res = await sendPutRequest({
			url: `${SERVER_IP}stock/grn/${filteredData[0]?._id}`,
			body: {
				orgId: globalRedux.selectedOrganization._id,
				grnNumber: formValues?.grnNumber,
				grnDate: formValues?.grnDate,
			},
		});
		console.log('🚀 ~ updateGRNNumber ~ res:', res);
		await setUpdatingData(false);
		await getBillingItems({ lrNumber: formValues?.lrNumber });
	}, [setUpdatingData, globalRedux?.selectedOrganization?._id, filteredData]);

	// const handleClear = () => {
	// 	setTableData([]);
	// 	form.resetFields();
	// };

	// if no table data reset form
	useEffect(() => {
		if (!tableData?.length) {
			// form.resetFields();
			form.setFieldsValue({
				grnNumber: '',
				grnDate: moment(),
			});
		}
	}, [tableData]);

	return (
		<>
			<div style={{ display: 'none' }}>
				<GRNUpdateToPrint ref={componentRef} data={selectedRecordToPrint} />
			</div>
			<GRNUpdatePresentational
				{...{
					column,
					filteredData,
					handleTableChange,
					getStartingValue,
					getEndingValue,
					pageSize,
					intialPageSizeOptions,
					initialPageSize,
					currentPage,
					getBillingItems,
					form,
					fetchingData,
					updateGRNNumber,
					updatingData,
				}}
			/>
		</>
	);
});

export default GRNUpdateFunctional;
